import { useState, useEffect } from 'react';
import { usePromoBoard, usePromoBoardMutations } from '../hooks/usePromoBoard';
import { useApi } from '../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';
import Drawer from '../components/promo/Drawer';

const WEEKS = Array.from({ length: 52 }, (_, i) => i + 1);
const COLW = 46;

const FACET = {
  price:      { label: 'Promo Price', color: 'var(--success)', base: 'promotion',        kind: 'promotion' },
  scan:       { label: 'Scan Deal',   color: 'var(--info)',    base: 'promotion',        kind: 'promotion' },
  media:      { label: 'Media',       color: 'var(--ai)',      base: 'promo-media',      kind: 'media' },
  activation: { label: 'Activation',  color: 'var(--warn)',    base: 'promo-activation', kind: 'activation' },
};
const MECHANICS = ['SinglePrice', 'PercentOff', 'MultiBuy', 'EDLP'];
const MEDIA_TYPES = ['CAT', 'Digital', 'Beacon', 'BusStops', 'Loyalty', 'OnlineOnly', 'Other'];
const DISPLAY_TYPES = ['', 'Endcap', 'Display', 'Fixture', 'NavBeacon', 'Ticket', 'Aisle', 'Other'];
const CHANNELS = ['RetailMedia', 'Meta', 'ATL', 'Social', 'Sampling', 'Sponsorship', 'FreeMedia', 'Other'];

// ── Band row (range entities → colSpan bands across 52 weeks) ────────────────
function BandRow({ label, bands, color, onBandClick, onAdd, editable }) {
  const sorted = [...(bands || [])].sort((a, b) => a.startWeek - b.startWeek);
  const cells = [];
  let w = 1;
  for (const band of sorted) {
    const start = Math.max(band.startWeek, w);
    const end = Math.min(band.endWeek, 52);
    if (end < start) continue;
    while (w < start) { cells.push(<td key={`g${w}`} style={{ minWidth: COLW, borderBottom: '1px solid var(--bg-elevated)' }} />); w++; }
    const draft = band.status === 'draft';
    cells.push(
      <td key={`b${w}`} colSpan={end - start + 1}
        onClick={editable ? () => onBandClick(band) : undefined}
        title={band.label}
        style={{
          background: `color-mix(in srgb, ${color} ${draft ? 12 : 22}%, transparent)`,
          border: `1px ${draft ? 'dashed' : 'solid'} color-mix(in srgb, ${color} 55%, transparent)`,
          borderRadius: 3, padding: '2px 5px', cursor: editable ? 'pointer' : 'default',
          overflow: 'hidden',
        }}>
        <span style={{ display: 'block', fontSize: 9, color, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: (end - start + 1) * COLW - 6 }}>
          {band.label}{draft ? ' •' : ''}
        </span>
      </td>);
    w = end + 1;
  }
  while (w <= 52) { cells.push(<td key={`t${w}`} style={{ minWidth: COLW, borderBottom: '1px solid var(--bg-elevated)' }} />); w++; }
  return (
    <tr>
      <td style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg-elevated)', padding: '2px 8px', fontSize: 10, fontWeight: 500, color, borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
        {label}
        {editable && onAdd && <button onClick={onAdd} title="Add" style={{ marginLeft: 6, border: '1px solid var(--border-soft)', background: 'none', borderRadius: 3, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, padding: '0 5px' }}>+</button>}
      </td>
      {cells}
    </tr>
  );
}

function WeekHeaders({ calendar }) {
  const dateFor = (w) => { const c = calendar?.find((x) => x.week === w); return c ? c.startDate.slice(5) : ''; };
  // Current fiscal week = the most-recently-started week ≤ today (derived, not
  // hardcoded — the old CURRENT_WEEK=37 had drifted 11 weeks out of date).
  const _today = new Date().toISOString().slice(0, 10);
  const currentWeek = (calendar || []).filter((c) => c.startDate <= _today).sort((a, b) => b.startDate.localeCompare(a.startDate))[0]?.week ?? 0;
  return (
    <tr style={{ background: 'var(--bg-elevated)', position: 'sticky', top: 0, zIndex: 3 }}>
      <th style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--bg-elevated)', width: 150, minWidth: 150, padding: '4px 8px', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>Row</th>
      {WEEKS.map((w) => (
        <th key={w} style={{ minWidth: COLW, width: COLW, textAlign: 'center', padding: '2px 1px', borderBottom: '1px solid var(--border)', borderTop: w === currentWeek ? '2px solid #6366f1' : undefined, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 9, fontWeight: 600 }}>W{w}</div>
          <div style={{ fontSize: 7 }}>{dateFor(w)}</div>
        </th>
      ))}
    </tr>
  );
}

function SectionHeader({ children }) {
  return <tr><td colSpan={53} style={{ background: 'var(--border)', padding: '5px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{children}</td></tr>;
}

// Read-only KeySellDates → one-week bands for the overlay row (shared by every view).
function milestoneBands(board) {
  return Object.entries(board.milestones).map(([wk]) => ({
    startWeek: +wk, endWeek: +wk,
    label: board.milestones[wk].map((m) => `${m.customer.split(' ')[0]}:${m.kind}`).join(' · '),
    status: 'published',
  }));
}

// ── Editor drawer (typed, per-kind) ──────────────────────────────────────────
function EditorDrawer({ ctx, accounts, onClose, onSave, onDelete, busy, error }) {
  const [f, setF] = useState(ctx.data);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const kind = ctx.kind;
  const title = `${ctx.data.id ? 'Edit' : 'New'} ${kind}`;
  return (
    <Drawer open title={title} onClose={onClose}
      footer={<>
        {ctx.data.id && <Button variant="destructive" size="sm" onClick={() => onDelete(FACET_BASE[kind], ctx.data.id)} disabled={busy}>Delete</Button>}
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={busy} onClick={() => onSave(kind, f)}>{busy ? 'Saving…' : 'Save'}</Button>
      </>}>
      {error && <div style={{ background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid var(--danger-border)', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>{String(error).slice(0, 200)}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}><Label>Start week</Label><Input type="number" min="1" max="52" value={f.startWeek} onChange={set('startWeek')} /></div>
        <div style={{ flex: 1 }}><Label>End week</Label><Input type="number" min="1" max="52" value={f.endWeek} onChange={set('endWeek')} /></div>
      </div>

      {kind === 'promotion' && <>
        <div><Label>Mechanic</Label><Select value={f.mechanicType} onChange={set('mechanicType')}>{MECHANICS.map((m) => <option key={m}>{m}</option>)}</Select></div>
        {(f.mechanicType === 'SinglePrice' || f.mechanicType === 'EDLP' || f.mechanicType === 'MultiBuy') &&
          <div><Label>{f.mechanicType === 'MultiBuy' ? 'Total price $' : 'Price $'}</Label><Input type="number" step="0.01" value={f.pricePoint ?? ''} onChange={set('pricePoint')} /></div>}
        {f.mechanicType === 'MultiBuy' && <div><Label>Qty</Label><Input type="number" value={f.qty ?? ''} onChange={set('qty')} /></div>}
        {f.mechanicType === 'PercentOff' && <div><Label>Discount %</Label><Input type="number" value={f.discountPct ?? ''} onChange={set('discountPct')} /></div>}
        <div><Label>Scan deal $/unit</Label><Input type="number" step="0.0001" value={f.scanDealPerUnit ?? ''} onChange={set('scanDealPerUnit')} /></div>
        <div><Label>Notes</Label><Input value={f.notes ?? ''} onChange={set('notes')} /></div>
      </>}
      {kind === 'media' && <>
        <div><Label>Media type</Label><Select value={f.mediaType} onChange={set('mediaType')}>{MEDIA_TYPES.map((m) => <option key={m}>{m}</option>)}</Select></div>
        <div><Label>Note</Label><Input value={f.note ?? ''} onChange={set('note')} /></div>
      </>}
      {kind === 'activation' && <>
        <div><Label>Code</Label><Input value={f.code ?? ''} onChange={set('code')} placeholder="e.g. FGE008" /></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Label>Store count</Label><Input type="number" value={f.storeCount ?? ''} onChange={set('storeCount')} /></div>
          <div style={{ flex: 1 }}><Label>Display</Label><Select value={f.displayType ?? ''} onChange={set('displayType')}>{DISPLAY_TYPES.map((d) => <option key={d} value={d}>{d || '—'}</option>)}</Select></div>
        </div>
        <div><Label>Note</Label><Input value={f.note ?? ''} onChange={set('note')} /></div>
      </>}
      {kind === 'campaign' && <>
        <div><Label>Channel</Label><Select value={f.channel} onChange={set('channel')}>{CHANNELS.map((c) => <option key={c}>{c}</option>)}</Select></div>
        <div><Label>Campaign name</Label><Input value={f.campaignName ?? ''} onChange={set('campaignName')} /></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Label>Brand</Label><Input value={f.brand ?? ''} onChange={set('brand')} placeholder="Bhuja / Infuzions" /></div>
          <div style={{ flex: 1 }}><Label>Account</Label><Select value={f.accountId ?? ''} onChange={set('accountId')}><option value="">(brand-level)</option>{accounts.map((a) => <option key={a.accountId} value={a.accountId}>{a.retailer}</option>)}</Select></div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Label>Budget $</Label><Input type="number" value={f.budget ?? ''} onChange={set('budget')} /></div>
          <div style={{ flex: 1 }}><Label>Spend $</Label><Input type="number" value={f.spend ?? ''} onChange={set('spend')} /></div>
        </div>
        <div><Label>Notes</Label><Input value={f.notes ?? ''} onChange={set('notes')} /></div>
      </>}
      <div><Label>Status</Label><Select value={f.status} onChange={set('status')}><option value="draft">Draft</option><option value="published">Published</option></Select></div>
    </Drawer>
  );
}
const FACET_BASE = { promotion: 'promotion', media: 'promo-media', activation: 'promo-activation', campaign: 'marketing-campaign' };

// ── Plan tab ─────────────────────────────────────────────────────────────────
function PlanTab({ board, fy, mut, canEdit, isAdmin }) {
  const apiFetch = useApi();
  const [label, setLabel] = useState('');
  const { data: snaps = [] } = useQuery({
    queryKey: ['plan-snapshots', fy],
    queryFn: async () => (await apiFetch(`/plan-snapshots?fy=${fy}`)).json(),
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
      <div className="card" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <Stat label="Promotions" value={board.totals.promotions} />
        <Stat label="Campaigns" value={board.totals.campaigns} />
        <Stat label="Marketing budget" value={`$${board.totals.budget.toLocaleString()}`} />
        <Stat label="Unpublished (draft)" value={board.totals.drafts} accent={board.totals.drafts ? 'var(--warn)' : 'var(--success)'} />
      </div>
      {canEdit && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Publish &amp; snapshot</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="primary" size="sm" disabled={mut.publish.isPending || !board.totals.drafts} onClick={() => mut.publish.mutate()}>
              {mut.publish.isPending ? 'Publishing…' : `Publish ${board.totals.drafts} draft change(s)`}
            </Button>
            {isAdmin && <>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Snapshot label (e.g. FY26 Q3 lock)" style={{ width: 240 }} />
              <Button variant="default" size="sm" disabled={!label.trim() || mut.snapshot.isPending} onClick={() => mut.snapshot.mutate(label.trim(), { onSuccess: () => setLabel('') })}>Snapshot</Button>
            </>}
          </div>
        </div>
      )}
      <div className="card">
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Plan snapshots</span>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {snaps.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No snapshots yet.</span>}
          {snaps.map((s) => (
            <div key={s.snapshotId} style={{ fontSize: 12, color: 'var(--text-body)', display: 'flex', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>{s.label}</span>
              <span style={{ color: 'var(--text-muted)' }}>{s.createdAt?.slice(0, 16).replace('T', ' ')} · {s.createdBy}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return <div><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div><div style={{ fontSize: 22, fontWeight: 700, color: accent || 'var(--text-primary)' }}>{value}</div></div>;
}

// ── Export ───────────────────────────────────────────────────────────────────
async function exportXlsx(board, fy) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const proms = board.raw.promotions.map((p) => ({ AssortmentId: p.assortmentId, Mechanic: p.mechanicType, StartWeek: p.startWeek, EndWeek: p.endWeek, Price: p.pricePoint, Qty: p.qty, DiscountPct: p.discountPct, ScanDeal: p.scanDealPerUnit, Status: p.status }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(proms), 'Promotions');
  const camps = board.raw.marketing.map((c) => ({ Channel: c.channel, Brand: c.brand, Campaign: c.campaignName, StartWeek: c.startWeek, EndWeek: c.endWeek, Budget: c.budget, Spend: c.spend, Status: c.status }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(camps), 'Marketing');
  XLSX.writeFile(wb, `Promotions_FY${fy}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function PromotionsV2({ view = 'sales' }) {
  const [fy, setFy] = useState(26);
  const { board, isLoading, error, isFetching } = usePromoBoard(fy);
  const mut = usePromoBoardMutations(fy);
  const [editMode, setEditMode] = useState(false);
  const [editor, setEditor] = useState(null); // {kind, data}
  const [showDates, setShowDates] = useState(true);
  const [exporting, setExporting] = useState(false);
  // Switching sidebar entry (sales/marketing/plan) drops edit affordances and any
  // open drawer so a stale editor can't carry across views.
  useEffect(() => { setEditMode(false); setEditor(null); }, [view]);

  const myRole = board?.myRole;
  const canSales = myRole === 'Admin' || myRole === 'SalesEditor';
  const canMkt = myRole === 'Admin' || myRole === 'MarketingEditor';

  function openNew(kind, extra) {
    const base = { startWeek: 1, endWeek: 1, status: 'draft', ...extra };
    const seed = {
      promotion: { mechanicType: 'SinglePrice' }, media: { mediaType: 'CAT' },
      activation: {}, campaign: { channel: 'RetailMedia', accountId: '' },
    }[kind];
    setEditor({ kind, data: { ...base, ...seed } });
  }
  function openEdit(kind, raw, extra) { setEditor({ kind, data: { ...raw, ...extra } }); }

  function handleSave(kind, f) {
    const base = FACET_BASE[kind];
    const payload = { id: f.id, rowVersion: f.rowVersion, startWeek: Number(f.startWeek), endWeek: Number(f.endWeek), status: f.status };
    if (kind === 'promotion') Object.assign(payload, { assortmentId: f.assortmentId, mechanicType: f.mechanicType, pricePoint: f.pricePoint, qty: f.qty, discountPct: f.discountPct, scanDealPerUnit: f.scanDealPerUnit, notes: f.notes });
    if (kind === 'media') Object.assign(payload, { assortmentId: f.assortmentId, mediaType: f.mediaType, note: f.note });
    if (kind === 'activation') Object.assign(payload, { assortmentId: f.assortmentId, code: f.code, storeCount: f.storeCount, displayType: f.displayType || null, note: f.note });
    if (kind === 'campaign') Object.assign(payload, { accountId: f.accountId || null, brand: f.brand, channel: f.channel, campaignName: f.campaignName, budget: f.budget, spend: f.spend, notes: f.notes });
    mut.save(base, payload, { onSuccess: () => setEditor(null) });
  }
  function handleDelete(base, id) { mut.remove(base, id, { onSuccess: () => setEditor(null) }); }
  async function handleExport() { setExporting(true); try { await exportXlsx(board, fy); } catch (e) { window.alert(`Export failed: ${e.message}`); } finally { setExporting(false); } }

  if (isLoading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading promotions…</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>Failed to load: {error.message}</div>;

  const editable = editMode && (view === 'sales' ? canSales : view === 'marketing' ? canMkt : false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {[26, 27].map((y) => <button key={y} className={fy === y ? 'pill-active' : 'pill-inactive'} onClick={() => setFy(y)}>FY{y}</button>)}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Role: <b>{myRole}</b>{isFetching ? ' · syncing…' : ''}</span>
          {view !== 'plan' && <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={showDates} onChange={(e) => setShowDates(e.target.checked)} />Sell dates</label>}
          <Button variant="ghost" size="sm" disabled={exporting} onClick={handleExport}>{exporting ? 'Exporting…' : 'Export'}</Button>
          {view !== 'plan' && (view === 'sales' ? canSales : canMkt) &&
            <Button variant={editMode ? 'primary' : 'default'} size="sm" onClick={() => { setEditMode((m) => !m); setEditor(null); }}>{editMode ? '✓ Done' : 'Edit'}</Button>}
        </div>
      </div>

      {mut.saveMut.error && <div style={{ background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid var(--danger-border)', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>{String(mut.saveMut.error.message)}</div>}

      {view === 'plan'
        ? <><PlanTab board={board} fy={fy} mut={mut} canEdit={canSales || canMkt} isAdmin={myRole === 'Admin'} /><CombinedCalendar board={board} fy={fy} /></>
        : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 2600, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup><col style={{ width: 150 }} />{WEEKS.map((w) => <col key={w} style={{ width: COLW }} />)}</colgroup>
                <thead><WeekHeaders calendar={board.calendar} /></thead>
                <tbody>
                  {showDates && <>
                    <SectionHeader>Key Sell Dates (read-only)</SectionHeader>
                    <BandRow label="Milestones" bands={milestoneBands(board)} color="var(--danger)" editable={false} />
                  </>}

                  {view === 'sales' && board.accounts.map((acc) => (
                    <SalesAccount key={acc.accountId} acc={acc} board={board} editable={editable} openNew={openNew} openEdit={openEdit} />
                  ))}

                  {view === 'marketing' && <>
                    <SectionHeader>Marketing campaigns — FY{fy}</SectionHeader>
                    {board.marketingLanes.length === 0 && <tr><td colSpan={53} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No campaigns yet{editable ? ' — add one below.' : '.'}</td></tr>}
                    {board.marketingLanes.map((lane) => (
                      <BandRow key={lane.channel} label={lane.channel} color="var(--ai)" bands={lane.bands} editable={editable}
                        onBandClick={(b) => openEdit('campaign', b.raw)} onAdd={() => openNew('campaign', { channel: lane.channel })} />
                    ))}
                    {editable && <tr><td colSpan={53} style={{ padding: '6px 10px' }}><Button variant="ai-soft" size="sm" onClick={() => openNew('campaign')}>+ Add campaign</Button></td></tr>}
                  </>}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {editor && (
        <EditorDrawer ctx={editor} accounts={board.accounts} busy={mut.saveMut.isPending || mut.removeMut.isPending} error={mut.saveMut.error?.message}
          onClose={() => setEditor(null)} onSave={handleSave} onDelete={handleDelete} />
      )}
    </div>
  );
}

// Consolidated read-only timeline for the "Activity Calendar" view: Key Sell
// Dates + every marketing lane + all sales rows on one 52-week grid. Authoring
// stays on the dedicated Promotions / Marketing Calendar views.
function CombinedCalendar({ board, fy }) {
  const noop = () => {};
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ minWidth: 2600, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup><col style={{ width: 150 }} />{WEEKS.map((w) => <col key={w} style={{ width: COLW }} />)}</colgroup>
          <thead><WeekHeaders calendar={board.calendar} /></thead>
          <tbody>
            <SectionHeader>Key Sell Dates (read-only)</SectionHeader>
            <BandRow label="Milestones" bands={milestoneBands(board)} color="var(--danger)" editable={false} />
            <SectionHeader>Marketing campaigns — FY{fy}</SectionHeader>
            {board.marketingLanes.length === 0 && <tr><td colSpan={53} style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No campaigns.</td></tr>}
            {board.marketingLanes.map((lane) => (
              <BandRow key={lane.channel} label={lane.channel} color="var(--ai)" bands={lane.bands} editable={false} />
            ))}
            {board.accounts.map((acc) => (
              <SalesAccount key={acc.accountId} acc={acc} board={board} editable={false} openNew={noop} openEdit={noop} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalesAccount({ acc, board, editable, openNew, openEdit }) {
  const assts = board.assortments.filter((a) => a.accountId === acc.accountId);
  return (
    <>
      <SectionHeader>{acc.retailer}</SectionHeader>
      {assts.map((a) => {
        const facets = board.byAsst.get(a.assortmentId) || { price: [], scan: [], media: [], activation: [] };
        const ctx = { assortmentId: a.assortmentId };
        return (
          <SalesAsst key={a.assortmentId} a={a} facets={facets} editable={editable}
            openNew={(kind) => openNew(kind, ctx)} openEdit={(kind, raw) => openEdit(kind, raw, ctx)} />
        );
      })}
    </>
  );
}
function SalesAsst({ a, facets, editable, openNew, openEdit }) {
  return (
    <>
      <tr><td colSpan={53} style={{ background: 'var(--bg-elevated)', padding: '4px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', borderTop: '2px solid var(--border)', position: 'sticky', left: 0 }}>
        {a.assortment} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 10 }}>{a.brand}</span>
      </td></tr>
      <BandRow label={FACET.price.label} color={FACET.price.color} bands={facets.price} editable={editable} onBandClick={(b) => openEdit('promotion', b.raw)} onAdd={() => openNew('promotion')} />
      <BandRow label={FACET.scan.label} color={FACET.scan.color} bands={facets.scan} editable={editable} onBandClick={(b) => openEdit('promotion', b.raw)} />
      <BandRow label={FACET.media.label} color={FACET.media.color} bands={facets.media} editable={editable} onBandClick={(b) => openEdit('media', b.raw)} onAdd={() => openNew('media')} />
      <BandRow label={FACET.activation.label} color={FACET.activation.color} bands={facets.activation} editable={editable} onBandClick={(b) => openEdit('activation', b.raw)} onAdd={() => openNew('activation')} />
    </>
  );
}
